import React, { useState, useRef } from 'react';
import { Building2, ChevronDown, Star, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useAuthStore from '../store/authStore';
import useOutsideClick from '../hooks/useOutsideClick';

/**
 * Top-header dropdown listing every center the user belongs to, with their role per center.
 * Hidden when the user has ≤1 active memberships (no point showing a switcher with one option).
 *
 * Actions:
 *  - Click a row → switches to that center (full page reload via authStore.switchCenter).
 *  - Star icon on a row → sets that center as the user's default for next login.
 */
export default function CenterSwitcher() {
  const { t, i18n } = useTranslation();
  const { memberships, centerId, defaultCenterId, switchCenter, setDefaultCenter } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [busyCenterId, setBusyCenterId] = useState(null);
  const ref = useRef(null);
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  useOutsideClick(ref, () => setIsOpen(false), isOpen);

  const active = (memberships || []).filter(m => m && (m.isActive !== false));
  if (active.length <= 1) return null;

  const current = active.find(m => m.centerId === centerId) || active[0];

  const roleLabel = (m) => {
    if (m.isManager) return t('roles.manager', 'Manager');
    const r = m.role;
    if (r === 'center_manager') return t('roles.manager', 'Manager');
    if (r === 'teacher') return t('roles.teacher', 'Teacher');
    if (r === 'admin') return t('roles.admin', 'Admin');
    if (r === 'worker') {
      return m.workerRole ? t(`workerRoles.${m.workerRole}`, m.workerRole) : t('roles.worker', 'Worker');
    }
    return r || '';
  };

  const handleSwitch = async (targetId) => {
    if (targetId === centerId) {
      setIsOpen(false);
      return;
    }
    try {
      setBusyCenterId(targetId);
      await switchCenter(targetId);
      // switchCenter reloads the page, so we won't actually get here normally.
    } catch (err) {
      setBusyCenterId(null);
    }
  };

  const handleSetDefault = async (e, targetId) => {
    e.stopPropagation();
    if (targetId === defaultCenterId) return;
    try {
      await setDefaultCenter(targetId);
    } catch (err) {
      // error already logged in the store
    }
  };

  return (
    // data-audit-skip: the UI-audit crawler (src/dev/uiAudit) must not switch centers.
    <div ref={ref} data-audit-skip style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('header.switchCenter', 'Switch center')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '6px 12px',
          backgroundColor: 'transparent',
          border: '1px solid var(--color-border, #E2E8F0)',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          color: 'var(--color-text-secondary, #64748B)',
          transition: 'background-color 0.2s ease',
          maxWidth: '220px'
        }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-hover, #F1F5F9)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      >
        <Building2 size={16} />
        <span
          style={{
            display: window.innerWidth > 768 ? 'inline' : 'none',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '140px'
          }}
        >
          {current?.centerName || current?.centerId || ''}
        </span>
        <ChevronDown
          size={14}
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease'
          }}
        />
      </button>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: isRTL ? 'auto' : 0,
            left: isRTL ? 0 : 'auto',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            minWidth: '280px',
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 60
          }}
        >
          <div
            style={{
              padding: '10px 16px',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: '#94A3B8',
              borderBottom: '1px solid #E2E8F0'
            }}
          >
            {t('header.yourCenters', 'Your centers')}
          </div>
          {active.map((m) => {
            const isCurrent = m.centerId === centerId;
            const isDefault = m.centerId === defaultCenterId;
            const isBusy = busyCenterId === m.centerId;
            return (
              <div
                key={m.centerId}
                onClick={() => !isBusy && handleSwitch(m.centerId)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px',
                  padding: '10px 16px',
                  cursor: isBusy ? 'wait' : 'pointer',
                  backgroundColor: isCurrent ? 'var(--color-hover, #F1F5F9)' : 'transparent',
                  borderBottom: '1px solid #F1F5F9',
                  opacity: isBusy ? 0.6 : 1,
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isCurrent) e.currentTarget.style.backgroundColor = 'var(--color-surface-alt)';
                }}
                onMouseLeave={(e) => {
                  if (!isCurrent) e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <div style={{ minWidth: 0, flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                  <div
                    style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--color-text-primary, #1E293B)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {m.centerName || m.centerId}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary, #64748B)', marginTop: '2px' }}>
                    {roleLabel(m)}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
                  {isCurrent && <Check size={16} style={{ color: '#10B981' }} />}
                  <button
                    onClick={(e) => handleSetDefault(e, m.centerId)}
                    title={
                      isDefault
                        ? t('header.defaultCenter', 'Default center')
                        : t('header.setDefaultCenter', 'Set as default')
                    }
                    aria-label={
                      isDefault
                        ? t('header.defaultCenter', 'Default center')
                        : t('header.setDefaultCenter', 'Set as default')
                    }
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Star
                      size={16}
                      style={{
                        color: isDefault ? '#F59E0B' : '#CBD5E1',
                        fill: isDefault ? '#F59E0B' : 'transparent'
                      }}
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
